import React, { useContext } from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { ArrowUturnLeftIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "@mui/material";
import { ChipCarousel } from "components/Environment/shared/Carousel/ChipCarousel";
import { GPUInstanceType } from "types/InstanceTypes";
import { OrgContext } from "contexts/OrgContext";
import { InstanceTypesContext } from "contexts/InstanceTypesContext";
import { isBrevCloud } from "utils/instanceTypes";
import { NoComputeRegistered } from "./NoComputeRegistered";
import { ComputeOverview } from "./ComputeOverview";

interface LaunchableComputeOverviewProps {
  intendedInstanceType: string | null;
  intendedDiskStorage: string | null;
  instanceTypeObject: GPUInstanceType | null;
  onSetNewCurrentInstanceType?: (
    instanceTypeObject: GPUInstanceType | null,
    diskStorage: string,
  ) => void;
  loadingInstance: boolean;
  diskStorage: string | null;
  name?: string;
  isBrevCloudNode?: boolean;
  cloudProviderContent?: React.ReactNode;
  onViewAllOptions?: () => void;
  onStorageChange?: (storage: string) => void;
  onStorageValidityChange?: (isValid: boolean) => void;
}

export const LaunchableComputeOverview: React.FC<
  LaunchableComputeOverviewProps
> = ({
  intendedInstanceType,
  intendedDiskStorage,
  instanceTypeObject,
  onSetNewCurrentInstanceType,
  loadingInstance,
  diskStorage,
  isBrevCloudNode,
  cloudProviderContent,
  onViewAllOptions,
  onStorageChange,
  onStorageValidityChange,
}) => {
  const orgContext = useContext(OrgContext);
  const instanceTypesContext = useContext(InstanceTypesContext);

  if (loadingInstance) {
    return (
      <div className="w-[225px] mx-auto">
        <ChipCarousel />
      </div>
    );
  }

  if (!instanceTypeObject) {
    if (isBrevCloudNode) {
      return <NoComputeRegistered />;
    }
    return (
      <div className="flex flex-col">
        <div className="flex flex-row items-center text-rose-500 mr-3">
          <ChipCarousel />
        </div>
        <div className="flex flex-row items-center text-rose-500 mr-3">
          <span className="text-sm">
            (An error occured fetching instance details)
          </span>
        </div>
      </div>
    );
  }

  const isBrevCloudNodeInstance = isBrevCloud(instanceTypeObject.type);
  const warningSection =
    intendedInstanceType !== instanceTypeObject.type &&
    onSetNewCurrentInstanceType ? (
      <div className="mt-2 px-3 pb-3">
        <div className="flex flex-row items-center text-red-500">
          <Tooltip
            placement="top"
            title="You have swapped to an instance different from what the author originally configured. This could result in unexpected behavior."
          >
            <div className="flex flex-row items-center">
              <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
              <span className="text-xs text-red-500">Instance Changed</span>
            </div>
          </Tooltip>
          <Tooltip title="Revert to the original instance" placement="top">
            <ArrowUturnLeftIcon
              className="h-4 w-4 ml-2 cursor-pointer text-highlight hover:text-highlightLighter"
              onClick={() => {
                const foundInstanceType =
                  instanceTypesContext.allInstancesAvailable.find(
                    (instanceType) =>
                      instanceType.type === intendedInstanceType,
                  );
                if (foundInstanceType) {
                  onSetNewCurrentInstanceType(
                    foundInstanceType,
                    intendedDiskStorage || "",
                  );
                } else {
                  onSetNewCurrentInstanceType(null, intendedDiskStorage || "");
                }
              }}
            />
          </Tooltip>
        </div>
      </div>
    ) : null;

  return (
    <ComputeOverview
      instanceTypeObject={instanceTypeObject}
      diskStorage={diskStorage}
      warningSection={warningSection}
      showCloudProvider={!isBrevCloudNodeInstance && !cloudProviderContent}
      cloudProviderContent={cloudProviderContent}
      intendedDiskStorage={intendedDiskStorage}
      onViewAllOptions={onViewAllOptions}
      onStorageChange={onStorageChange}
      onStorageValidityChange={onStorageValidityChange}
    />
  );
};
